import React from 'react';
import { Phone } from 'lucide-react';
import { usePhoneTracking } from './PhoneTrackingProvider';
import { logger } from '../utils/logger';

interface EnhancedPhoneButtonProps {
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  children?: React.ReactNode;
  trackingLabel?: string;
}

export default function EnhancedPhoneButton({
  className = '',
  variant = 'primary',
  size = 'md',
  showIcon = true,
  children,
  trackingLabel
}: EnhancedPhoneButtonProps) {
  const { trackPhoneClick } = usePhoneTracking();

  const variants = {
    primary: 'bg-secondary hover:bg-secondary-light text-primary',
    secondary: 'bg-primary hover:bg-primary-light text-white',
    outline: 'border-2 border-secondary text-secondary hover:bg-secondary hover:text-primary'
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const handleClick = () => {
    // Tracking is handled automatically by the phoneTracker singleton
    // No need to call trackPhoneClick here to avoid duplicate tracking
    logger.debug('Phone button clicked:', trackingLabel);
  };

  return (
    <a
      href="tel:+16155063451"
      onClick={handleClick}
      className={`
        inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200
        transform hover:scale-105 active:scale-95
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
      aria-label={trackingLabel || "Call Pinnacle Home Theater at (615) 506-3451"}
      data-tracking-label={trackingLabel}
    >
      {showIcon && (
        <Phone className={`${iconSizes[size]} ${children ? 'mr-2' : ''}`} />
      )}
      {children || '(615) 506-3451'}
    </a>
  );
}